import React, { useMemo, useState, useCallback } from 'react';
import { LayoutPage, LayoutContent, Form, FormRenderer, WarningModal, Nudge } from 'components';
import { useStoreValue } from 'store';
import { initFormConfig, submitHandler, byCountry, resumeSavedEmploymentDetails } from 'utils';
import { config } from '_config';
import { isAU } from 'utils/byConfig';
import { getIncomeThresholdError } from 'utils/getAnnualIncome';
import { useWarning } from '_config/au/employmentDetails';
import { useFormInitialValues, useSteps } from '../../hooks';

const FORM_ID = 'employmentDetails';

const withIncomeThresholdValidation = (fields, countryCode) =>
  fields.map(field => {
    const fieldWithNestedValidation = field.fields
      ? { ...field, fields: withIncomeThresholdValidation(field.fields, countryCode) }
      : field;

    if (fieldWithNestedValidation.name !== 'income' || typeof fieldWithNestedValidation.validate !== 'function') {
      return fieldWithNestedValidation;
    }

    const validateIncome = fieldWithNestedValidation.validate;
    return {
      ...fieldWithNestedValidation,
      validate: (value, validationData = {}) => {
        const validationError = validateIncome(value, validationData);
        if (validationError) {
          return validationError;
        }

        return getIncomeThresholdError(validationData.data?.values, countryCode);
      },
    };
  });

export function EmploymentDetails() {
  const [storeState] = useStoreValue();
  const { acquisition } = storeState;
  let initialValues = useFormInitialValues(FORM_ID);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const onModalClose = useCallback(() => setWarningModalOpen(false), [setWarningModalOpen]);
  const { check, warnings } = useWarning();
  const formConfig = useMemo(() => {
    const countryCode = isAU() ? 'AU' : 'NZ';
    return withIncomeThresholdValidation(initFormConfig(config.formConfigs[FORM_ID], storeState), countryCode);
  }, [storeState]);
  const { goNext } = useSteps();

  initialValues = resumeSavedEmploymentDetails(initialValues);

  const onSubmit = useCallback(
    ({ values, errors, setErrors }) => {
      const incomeThresholdError = getIncomeThresholdError(values, isAU() ? 'AU' : 'NZ');

      if (incomeThresholdError) {
        setErrors({ income: incomeThresholdError });
        return;
      }

      function updateStoreAndRedirect() {
        const newAcquisition = {
          ...acquisition,
          employmentDetails: { ...values },
        };

        goNext({
          storePatch: { acquisition: newAcquisition },
          dataLayerEventLabel: byCountry({
            AU: 'Next',
            NZ: 'Next',
          }),
        });
      }

      submitHandler({ submit: updateStoreAndRedirect, errors });
    },
    [acquisition, goNext],
  );

  const onClickNext = useCallback(
    ({ values, errors, setErrors }) => {
      const incomeThresholdError = getIncomeThresholdError(values, isAU() ? 'AU' : 'NZ');

      if (incomeThresholdError) {
        setErrors({ income: incomeThresholdError });
        return;
      }

      if (Object.keys(errors).length > 0) {
        return;
      }

      if (isAU() && !check(values)) {
        setWarningModalOpen(true);
      } else {
        onSubmit({ values, errors, setErrors });
      }
    },
    [check, onSubmit],
  );

  return (
    <LayoutPage noSessionTimeout>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={onClickNext}
        showSaveDraftButton
        saveDraftOnSubmit
        saveDraftOnSessionTimeout
      >
        {formData => (
          <LayoutContent>
            <Nudge />
            <FormRenderer config={formConfig} formData={formData} storeState={storeState} />
            <WarningModal
              title="Can you confirm these finances are correct?"
              isOpen={isWarningModalOpen}
              onClose={onModalClose}
              onSubmit={() => onSubmit(formData.state)}
              warnings={warnings}
              editButtonText="Edit finances"
              confirmButtonText="Confirm and continue"
            />
          </LayoutContent>
        )}
      </Form>
    </LayoutPage>
  );
}
